import { useState } from 'react';

import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { ICartEntry, MinimalCartEntryType } from 'features/cart/types';
import { calculateEntryTotalPrice } from 'features/cart/utils';
import { StyleSheet, Text, View } from 'react-native';
import { Icon } from 'ucl/components/icon/Icon';

import { ActionButtonVariants } from 'components/action-button';
import Picture from 'components/picture';
import { InlineModal } from 'components/ui/inline-modal';
import { selectors, useAppSelector } from 'state/global-state';
import { primitive } from 'styles/constants/primitives';

import { CartDebugActionButton } from './cart-debug-action-button';
import { CartSanityButton } from './cart-sanity-button';

const BooleanBadge = ({ value, label }: { value: boolean; label: string }) => {
  return (
    <View
      style={{
        backgroundColor: value ? primitive.bk.$successLight : primitive.$blackOpacity10,
        paddingHorizontal: 5,
        paddingVertical: 2,
        borderRadius: 5,
      }}
    >
      <Text style={{ fontSize: 11, fontWeight: '500', textAlign: 'center' }}>{label}</Text>
    </View>
  );
};

const VerticalDivider = () => (
  <View style={{ width: 1, height: '100%', backgroundColor: primitive.$blackOpacity30 }} />
);

const CartDebugItemModal = ({
  cartEntry,
  isOpen,
  onClose,
  cartEntryType,
}: {
  cartEntry: ICartEntry;
  isOpen: boolean;
  onClose: () => void;
  cartEntryType: string;
}) => {
  return (
    <InlineModal
      isOpen={isOpen}
      onClose={onClose}
      header={{ title: `Debug ${cartEntryType}`, variant: 'light' }}
    >
      <View style={{ flexDirection: 'row', gap: 6, alignItems: 'flex-start', marginBottom: 10 }}>
        <View style={{ flexDirection: 'row', gap: 6, alignItems: 'center', flex: 1 }}>
          <View style={{ flexDirection: 'column', gap: 6 }}>
            <Picture
              key={cartEntry.image.asset?._id || cartEntry.id}
              size="$10"
              alt={cartEntry.image.locale?.imageDescription || ''}
              image={cartEntry.image}
            />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontWeight: 'bold', fontSize: 10, color: primitive.$blackOpacity70 }}>
              {cartEntry.id}
            </Text>
            <Text
              style={{ fontSize: 14, color: primitive.$black, fontWeight: '500', marginBottom: 5 }}
            >
              {cartEntry.name}
            </Text>
            <View style={{ flexDirection: 'row', gap: 6, marginTop: 0, marginBottom: 6 }}>
              <BooleanBadge value={true} label={cartEntryType} />
            </View>
            <View style={{ flexDirection: 'row', gap: 6, marginVertical: 6 }}>
              <Text style={{ fontFamily: 'monospace' }}>qty: {cartEntry.quantity}</Text>
              <VerticalDivider />
              <Text style={{ fontFamily: 'monospace' }}>
                price: {calculateEntryTotalPrice(cartEntry)}
              </Text>
            </View>
          </View>
        </View>
        <CartSanityButton id={cartEntry.id} />
      </View>
      {cartEntry.children.map(child => (
        <CartDebugItem key={child.id} cartEntry={child} isDebugModalOpen={true} />
      ))}
    </InlineModal>
  );
};

export const CartDebugItem = ({
  cartEntry,
  isDebugModalOpen,
}: {
  cartEntry: ICartEntry;
  isDebugModalOpen?: boolean;
}) => {
  const [debugItem, setDebugItem] = useState<string>();
  const { removeItemFromMinimalCart } = useMinimalCart();
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const rewardReference = availableLoyaltyRewardsMap[cartEntry.id];

  return (
    <View style={styles.container}>
      <View style={{ flexDirection: 'row', gap: 6 }}>
        <View style={{ flex: 1, flexDirection: 'row', gap: 6, alignItems: 'center' }}>
          <View style={{ flexDirection: 'column', gap: 6 }}>
            <Picture
              key={cartEntry.image.asset?._id || cartEntry.id}
              size="$10"
              alt={cartEntry.image.locale?.imageDescription || ''}
              image={cartEntry.image}
            />
          </View>
          <View style={{ flex: 1 }}>
            <Text style={{ fontWeight: 'bold', fontSize: 10, color: primitive.$blackOpacity70 }}>
              {cartEntry.id}
            </Text>
            <Text
              style={{ fontSize: 14, color: primitive.$black, fontWeight: '500', marginBottom: 5 }}
            >
              {cartEntry.name}
            </Text>
            <View style={{ flexDirection: 'row', gap: 6, marginTop: 0, marginBottom: 8 }}>
              <BooleanBadge value={true} label={cartEntry.type} />
              {!isDebugModalOpen && <BooleanBadge value={false} label="isFavorite" />}
              {!isDebugModalOpen && rewardReference && (
                <BooleanBadge value={true} label="isRewardEligible" />
              )}
            </View>
          </View>
        </View>
        <View style={{ flexDirection: 'column', gap: 5 }}>
          <CartSanityButton id={cartEntry.id} />
          {cartEntry.children.length > 0 && (
            <CartDebugActionButton
              background={primitive.$blackOpacity10}
              leftIcon={<Icon name="mi:settings" />}
              variant={ActionButtonVariants.TEXT_ONLY}
              onPress={() => setDebugItem(cartEntry.id)}
            >
              Debug
            </CartDebugActionButton>
          )}
        </View>
      </View>

      <View style={{ flexDirection: 'row', gap: 6, marginVertical: 6 }}>
        <Text style={{ fontFamily: 'monospace' }}>qty: {cartEntry.quantity}</Text>
        <VerticalDivider />
        <Text style={{ fontFamily: 'monospace' }}>
          price: {calculateEntryTotalPrice(cartEntry)}
        </Text>
        {rewardReference && (
          <>
            <VerticalDivider />
            <Text style={{ fontFamily: 'monospace' }}>pointCost: {rewardReference?.pointCost}</Text>
          </>
        )}
      </View>

      {!isDebugModalOpen && (
        <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
          {cartEntry.type !== MinimalCartEntryType.donation &&
            cartEntry.type !== MinimalCartEntryType.extra && (
              <CartDebugActionButton linkPath={cartEntry.url}>edit</CartDebugActionButton>
            )}
          {cartEntry.type !== MinimalCartEntryType.donation &&
            cartEntry.type !== MinimalCartEntryType.extra && (
              <CartDebugActionButton linkPath={cartEntry.url}>duplicate</CartDebugActionButton>
            )}
          <CartDebugActionButton onPress={() => removeItemFromMinimalCart(cartEntry)}>
            delete
          </CartDebugActionButton>
          {rewardReference && <CartDebugActionButton>apply reward</CartDebugActionButton>}
        </View>
      )}

      <CartDebugItemModal
        isOpen={!!debugItem}
        onClose={() => setDebugItem(undefined)}
        cartEntry={cartEntry}
        cartEntryType={cartEntry.type}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    padding: 10,
    borderRadius: 8,
    marginBottom: 8,
  },
});
